import type { InfiniteData } from '@tanstack/react-query';
import dayjs from 'dayjs';

import type { OpenMetadataResponse } from '@/shared/api/openmetadata';

import type { SearchResult } from '@/entities/search';
import { filterAndMapTags } from '@/entities/search/lib/tagUtils';

type AdditionalFields = {
  serviceType?: string;
  owners?: unknown[];
  experts?: unknown[];
  followers?: unknown[];
  assets?: unknown[];
};

/**
 * InfiniteQuery의 OpenMetadata 응답을 SearchResult 배열로 변환하는 공통 유틸
 *
 * @param data - InfiniteQuery로부터 받은 데이터
 * @param type - SearchResult의 type 필드
 * @param additionalMapper - 추가 필드를 매핑하는 옵셔널 함수
 * @returns SearchResult 배열
 */
export const transformInfiniteDataToSearchResults = <T extends SearchResult>(
  data: InfiniteData<OpenMetadataResponse> | undefined,
  type: SearchResult['type'] | 'auto',
  additionalMapper?: (hit: OpenMetadataResponse['hits']['hits'][0]) => AdditionalFields
): T[] => {
  if (!data?.pages) return [];

  return data.pages.flatMap((page: OpenMetadataResponse) => {
    return page.hits.hits.map((hit) => {
      const additionalFields = additionalMapper?.(hit) || {};

      return {
        id: hit._id,
        type: type === 'auto' ? (hit._source.entityType as SearchResult['type']) : type,
        title: hit._source.displayName || hit._source.name,
        description: hit._source.description || '',
        path: hit._source.fullyQualifiedName,
        owner: {
          name: hit._source.owners?.[0]?.name || '',
          department: '',
          email: '',
        },
        tags: filterAndMapTags(hit._source.tags),
        tier: hit._source.tier || undefined,
        certification:
          hit._source.certification?.tagLabel?.name && hit._source.certification.tagLabel.tagFQN
            ? {
                name: hit._source.certification.tagLabel.name,
                tagFQN: hit._source.certification.tagLabel.tagFQN,
                description: hit._source.certification.tagLabel.description || undefined,
                expiryDate: hit._source.certification.expiryDate,
                appliedDate: hit._source.certification.appliedDate,
                style: hit._source.certification.tagLabel.style || undefined,
              }
            : undefined,
        updatedAt: dayjs(hit._source.updatedAt).format('YYYY-MM-DD'),
        domains: hit._source.domains,
        _source: hit._source,
        ...additionalFields,
      } as T;
    });
  });
};
